import React, { useEffect, useState, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Truck, ArrowRight, Phone, Clock, MapPin } from 'lucide-react';
import { useT } from '../utils/i18n';
import { loadLatestOrder, decodeOrderToken, saveOrderForTracking, TrackedOrder } from '../utils/orderTracking';
// Lightweight entry point for tracking. The actual tracking page lives
// at /track/:orderId. This page (at /track) lets returning customers
// either resume their most recent order (pulled from localStorage) or
// punch in any order ID directly.
export function TrackOrderLookup() {
  const navigate = useNavigate();
  const t = useT();
  const [orderId, setOrderId] = useState('');
  const [recent, setRecent] = useState<TrackedOrder | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    // If the user arrived via a shared tracking link (?d=<token>) hitting /track,
    // decode it, persist it locally, then jump straight to the order page.
    try {
      const hash = window.location.hash;
      const qIdx = hash.indexOf('?');
      if (qIdx >= 0) {
        const params = new URLSearchParams(hash.slice(qIdx));
        const token = params.get('d');
        if (token) {
          const decoded = decodeOrderToken(token);
          if (decoded) {
            saveOrderForTracking(decoded);
            navigate(`/track/${encodeURIComponent(decoded.orderId)}?d=${token}`, {
              replace: true
            });
            return;
          }
        }
      }
    } catch {
      /* ignore */
    }
    // Otherwise show the most recent locally-saved order
    const latest = loadLatestOrder();
    if (latest) setRecent(latest);
  }, [navigate]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = orderId.trim();
    if (!cleaned) return;
    navigate(`/track/${encodeURIComponent(cleaned)}`);
  };
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2896">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2897">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="text-center mb-10" data-id="element-2898">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#1DB954]/10 text-[#1DB954] mb-5" data-id="element-2899">
            <Truck className="w-7 h-7" data-id="element-2900" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-2901">
            {t('track.title')}
          </h1>
          <p className="text-stone-600 dark:text-stone-400 max-w-md mx-auto" data-id="element-2902">
            {t('track.subtitle')}
          </p>
        </motion.div>

        {/* Recent order card — updated to read from unified TrackedOrder shape */}
        {recent && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="mb-8" data-id="element-2903">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 dark:text-stone-400 mb-3" data-id="element-2904">
              {t('track.recentOrder')}
            </p>
            <Link to={`/track/${recent.orderId}`} className="group block bg-white dark:bg-[#222] border border-stone-200 dark:border-[#333] rounded-2xl p-5 hover:border-[#1DB954]/40 dark:hover:border-[#1DB954]/40 transition-all shadow-sm hover:shadow-md" data-id="element-2905">
              <div className="flex items-center justify-between gap-4" data-id="element-2906">
                <div className="min-w-0 flex-1" data-id="element-2907">
                  <p className="font-heading text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2908">
                    {t('track.orderNum')}
                    {recent.orderId}
                  </p>
                  {recent.address && <p className="text-sm text-stone-600 dark:text-stone-400 flex items-center gap-1.5 mb-1" data-id="element-2909">
                      <MapPin className="w-3.5 h-3.5 shrink-0" data-id="element-2910" />
                      <span className="truncate" data-id="element-2911">{recent.address}</span>
                    </p>}
                  {recent.date && <p className="text-xs text-stone-500 dark:text-stone-500 flex items-center gap-1.5" data-id="element-2912">
                      <Clock className="w-3 h-3 shrink-0" data-id="element-2913" />
                      {new Date(recent.date).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
                      {recent.time ? ` · ${recent.time}` : ''}
                    </p>}
                </div>
                <div className="inline-flex items-center gap-1.5 text-[#1DB954] font-bold text-sm shrink-0" data-id="element-2914">
                  {t('track.continue')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-2915" />
                </div>
              </div>
            </Link>
          </motion.div>}

        {/* Lookup form */}
        <motion.form initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.15
      }} onSubmit={submit} className="bg-white dark:bg-[#222] border border-stone-200 dark:border-[#333] rounded-2xl p-6 shadow-sm" data-id="element-2916">
          <label className="block text-xs font-bold uppercase tracking-wider text-stone-600 dark:text-stone-300 mb-2" data-id="element-2917">
            {recent ? t('track.lookupAnother') : t('track.enterOrder')}
          </label>
          <div className="relative" data-id="element-2918">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2919">
              <Search className="w-4 h-4 text-stone-400 dark:text-stone-500" data-id="element-2920" />
            </div>
            <input type="text" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder={t('track.placeholder')} className="w-full pl-10 pr-4 py-3.5 bg-stone-50 dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#444] rounded-lg text-brand-charcoal dark:text-white placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-[#1DB954] focus:border-transparent transition-all" autoFocus={!recent} data-id="element-2921" />
          </div>
          <button type="submit" disabled={!orderId.trim()} className="mt-4 w-full bg-[#1DB954] hover:bg-[#17A347] disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 rounded-lg font-bold text-sm transition-colors flex items-center justify-center" data-id="element-2922">
            {t('track.button')}
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-2923" />
          </button>
          <p className="text-[11px] text-stone-500 dark:text-stone-500 mt-3 text-center" data-id="element-2924">
            {t('track.note')}
          </p>
        </motion.form>

        {/* Help */}
        <div className="mt-8 text-center" data-id="element-2925">
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-2" data-id="element-2926">
            {t('track.help.title')}
          </p>
          <a href="tel:206-619-3742" className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:underline" data-id="element-2927">
            <Phone className="w-4 h-4" data-id="element-2928" />
            {t('track.help.call')}
          </a>
        </div>
      </div>
    </main>;
}